import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { notify } from '../utils/notify';
import '../styles/bpo.css';

// Conciliação Omie (BPO Financeiro, 26/10/2026): compara a planilha de fluxo do cliente
// (uma aba por mês, "Local Caixa" = conta) com o extrato da conta no Omie e permite
// Lançar / Excluir / Refazer UM lançamento por clique. Sem banco: a planilha fica só
// no estado desta página. Spec: docs/superpowers/specs/2026-10-26-conciliacao-omie-design.md

const URL = `${process.env.REACT_APP_API_BASE}/api/bpofin/conciliacao/`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const brl = (v) => (v == null ? '-' : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v));
const dataBr = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '-');
const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
const ehTransf = (d) => /transfer[eê]ncia\s*\(.+>.+\)/i.test(d || '');
const msgErro = (e) => e?.response?.data?.mensagem || e?.message || 'Falha inesperada';
// Classificação do cliente na planilha (Tipo · Departamento · Categoria) e o nível mais fino
// preenchido, usado para procurar a categoria parecida no Omie.
const classifPlanilha = (l) => [l.tipo, l.departamento, l.categoria].filter(Boolean).join(' · ');
const chavePre = (l) => l.categoria || l.departamento || l.tipo || '';

// Modal no padrão do Saber (header branco, sem window.confirm).
function Modal({ titulo, perigo, children, rodape, onFechar, ocupado }) {
  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)', zIndex: 1080 }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content shadow-lg">
          <div className="modal-header" style={{ backgroundColor: '#fff', borderBottom: '1px solid #e9ecef' }}>
            <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
              <i className={`fas ${perigo ? 'fa-triangle-exclamation text-danger' : 'fa-circle-check'} me-2`} style={perigo ? undefined : { color: '#00b5cc' }}></i>{titulo}
            </h5>
            <button type="button" className="btn-close" onClick={onFechar} disabled={ocupado}></button>
          </div>
          <div className="modal-body">{children}</div>
          <div className="modal-footer bg-light">{rodape}</div>
        </div>
      </div>
    </div>
  );
}

export default function BpoFinConciliacaoOmie() {
  const arquivoRef = useRef(null);
  const resultadoRef = useRef(null);
  const [planilha, setPlanilha] = useState(null);        // {abas, contas, nome}
  const [contasOmie, setContasOmie] = useState([]);      // de-para do backend
  const [aba, setAba] = useState('');
  const [conta, setConta] = useState('');
  const [resultado, setResultado] = useState(null);      // {saldos, so_planilha, so_omie, pareados}
  const [resumo, setResumo] = useState(null);            // revisão do mês: [{conta, status, ...}]
  const [catLinha, setCatLinha] = useState({});          // idx da linha da planilha → código de categoria escolhido na tabela
  // O extrato do Omie demora alguns segundos para refletir uma escrita; enquanto isso a linha
  // fica marcada aqui: lancados[idx da planilha] = nCodLanc, excluidos[nCodLanc] = true.
  const [feitos, setFeitos] = useState({ lancados: {}, excluidos: {} });
  const [selecionados, setSelecionados] = useState([]);  // nCodLancamento marcados em "Só no Omie" para excluir em lote
  const [selLancar, setSelLancar] = useState([]);        // idx das linhas marcadas em "Só na planilha" para lançar em lote
  const [lote, setLote] = useState(null);                // progresso do lote: {total, feitos, atual, erros: [{cod, erro}], pernas: []}
  const [categorias, setCategorias] = useState({ R: null, D: null });
  const [ocupado, setOcupado] = useState('');            // 'planilha' | 'resumo' | 'comparar' | 'acao'
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [modal, setModal] = useState(null);              // {tipo:'lancar'|'refazer'|'excluir'|'escolher'|'perna', ...}
  const [filtroPlan, setFiltroPlan] = useState('faltam');   // 'faltam' | 'omie' | 'todas' — linhas da planilha mostradas
  // Observações da revisão (texto livre por mês+conta), gravadas no banco (bpofin_conciliacao_obs) e
  // compartilhadas entre operadores. Edição local enquanto digita; grava ao sair do campo ou no Enter.
  const [obs, setObs] = useState({});                    // conta → {texto, por, em}
  const [obsSujas, setObsSujas] = useState({});          // conta → texto ainda não gravado
  const obsDe = (c) => (obsSujas[c] !== undefined ? obsSujas[c] : (obs[c]?.texto || ''));
  const carregarObs = async (a) => {
    if (!a) { setObs({}); return; }
    try {
      const { data } = await axios.get(URL, { params: { acao: 'obs_listar', ano: a.ano, mes: a.mes }, headers: headers() });
      setObs(data.obs || {});
    } catch (err) { notify.erro(`Não carreguei as observações: ${msgErro(err)}`); }
  };
  const gravarObs = async (c, texto, a = abaAtual) => {
    if (!a) return;
    const limpo = (texto || '').trim();
    setObsSujas((p) => { const n = { ...p }; delete n[c]; return n; });
    if (limpo === (obs[c]?.texto || '') && a === abaAtual) return;
    try {
      await axios.post(URL, { acao: 'obs_gravar', ano: a.ano, mes: a.mes, conta: c, texto: limpo }, { headers: headers() });
      if (a === abaAtual) setObs((p) => { const n = { ...p }; if (limpo) n[c] = { texto: limpo, por: 'você', em: 'agora' }; else delete n[c]; return n; });
    } catch (err) { notify.erro(`Não gravei a observação: ${msgErro(err)}`); }
  };
  // Migração única: anotações feitas na versão anterior ficavam no navegador; sobem para o banco e saem daqui.
  useEffect(() => {
    if (!planilha) return;
    let antigas = {};
    try { antigas = JSON.parse(localStorage.getItem('bpofin_conciliacao_obs') || '{}'); } catch (e) { antigas = {}; }
    const pares = Object.entries(antigas).filter(([, t]) => t);
    if (!pares.length) return;
    (async () => {
      let n = 0;
      for (const [chave, texto] of pares) {
        const [nomeAba, c] = chave.split('|');
        const a = planilha.abas.find((x) => x.nome === nomeAba);
        if (!a) continue;
        try {
          await axios.post(URL, { acao: 'obs_gravar', ano: a.ano, mes: a.mes, conta: c, texto }, { headers: headers() });
          n += 1;
        } catch (err) { notify.erro(`Não migrei a observação de ${c} (${nomeAba}): ${msgErro(err)}`); return; }
      }
      try { localStorage.removeItem('bpofin_conciliacao_obs'); } catch (e) { /* sem armazenamento */ }
      if (n) { notify.sucesso(`${n} observação(ões) do navegador gravada(s) no banco`); carregarObs(abaAtual); }
    })();
  }, [planilha]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    axios.get(URL, { params: { acao: 'contas' }, headers: headers() })
      .then((r) => setContasOmie(r.data.contas || []))
      .catch((e) => setErro(msgErro(e)));
  }, []);

  const abaAtual = useMemo(() => planilha?.abas.find((a) => a.nome === aba) || null, [planilha, aba]);
  const temDePara = (nome) => contasOmie.some((c) => norm(c.nome) === norm(nome));
  const linhasSoPlanilha = resultado?.so_planilha || [];
  // Todas as linhas da conta no mês, com o estado de cada uma: falta no Omie ou já está (com o par).
  const linhasPlanilha = useMemo(() => {
    if (!resultado) return [];
    const todas = [...resultado.so_planilha.map((l) => ({ l, par: null })),
                   ...resultado.pareados.map((p) => ({ l: p.planilha, par: p.omie }))];
    return todas.sort((a, b) => a.l.idx - b.l.idx);
  }, [resultado]);
  const linhasFiltradas = linhasPlanilha.filter((x) => (filtroPlan === 'todas' ? true : filtroPlan === 'omie' ? !!x.par : !x.par));

  const importar = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setOcupado('planilha'); setErro(''); setAviso(''); setResultado(null);
    const fd = new FormData();
    fd.append('arquivo', f);
    try {
      const { data } = await axios.post(URL, fd, { headers: headers(), timeout: 240000 });
      setPlanilha({ ...data, nome: f.name });
      setAba(data.abas[data.abas.length - 1]?.nome || '');
      setConta('');
    } catch (err) { setErro(msgErro(err)); } finally { setOcupado(''); }
  };

  // Revisão do mês: um extrato por conta no Omie, só saldo e contagens. Roda ao escolher o mês.
  const revisar = async (a) => {
    if (!a) return;
    setOcupado('resumo'); setErro(''); setResumo(null);
    try {
      const { data } = await axios.post(URL, {
        acao: 'resumo', ano: a.ano, mes: a.mes, linhas: a.linhas, saldos: a.saldos,
      }, { headers: headers(), timeout: 300000 });
      setResumo(data.contas);
    } catch (err) { setErro(msgErro(err)); } finally { setOcupado(''); }
  };

  useEffect(() => { revisar(abaAtual); setObsSujas({}); carregarObs(abaAtual); }, [abaAtual]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Análise da diferença: percorre todas as abas até o mês escolhido e aponta onde o saldo descola e a causa provável.
  const ROTULO_CAUSA = {
    abertura: 'abertura digitada na planilha', valor_diferente: 'mesmo dia com valor diferente', so_omie: 'sobra no Omie',
    so_planilha: 'falta no Omie', outra_conta: 'valor em outra conta', indefinida: 'lançamentos batem, conferir extrato',
  };
  const analisar = async (c) => {
    if (!abaAtual || !planilha) return;
    const abas = planilha.abas
      .filter((a) => a.ano < abaAtual.ano || (a.ano === abaAtual.ano && a.mes <= abaAtual.mes))
      .map((a) => ({ nome: a.nome, ano: a.ano, mes: a.mes, linhas: a.linhas, saldos: a.saldos }));
    setModal({ tipo: 'analise', conta: c, dados: null, erro: '' });
    setOcupado('analise');
    try {
      const { data } = await axios.post(URL, { acao: 'analisar', conta: c, abas }, { headers: headers(), timeout: 300000 });
      setModal((m) => (m && m.tipo === 'analise' && m.conta === c ? { ...m, dados: data } : m));
    } catch (err) {
      setModal((m) => (m && m.tipo === 'analise' && m.conta === c ? { ...m, erro: msgErro(err) } : m));
    } finally { setOcupado(''); }
  };
  const obsDaAnalise = (d) => {
    if (!d || !d.inicio) return 'Saldo bate em todos os meses';
    const rotulos = [...new Set(d.mudancas.flatMap((mu) => mu.causas.map((c) => ROTULO_CAUSA[c.tipo] || c.tipo)))];
    return `Nasce em ${d.inicio.nome} (${brl(d.dif_final)}): ${rotulos.join(', ')}`;
  };
  const abrirMes = (nome, c) => {
    const a = planilha?.abas.find((x) => x.nome === nome);
    if (!a) return;
    setModal(null); setAba(nome); setConta(c); setFeitos({ lancados: {}, excluidos: {} });
    compararConta(c, a);
  };

  const compararConta = async (c, a) => {
    if (!a || !c) return;
    setOcupado('comparar'); setErro('');
    try {
      const { data } = await axios.post(URL, {
        acao: 'comparar', conta: c, ano: a.ano, mes: a.mes,
        linhas: a.linhas.filter((l) => l.conta === c), saldos: a.saldos[c] || {},
      }, { headers: headers(), timeout: 120000 });
      setResultado(data);
      setCatLinha({});
      setSelecionados([]);
      setSelLancar([]);
      // Categorias do Omie para o select da tabela (receitas e despesas); falha aqui não trava a tela.
      if (data.so_planilha.length) {
        const precisaR = data.so_planilha.some((l) => l.valor >= 0 && !ehTransf(l.descricao));
        const precisaD = data.so_planilha.some((l) => l.valor < 0 && !ehTransf(l.descricao));
        (async () => {
          try {
            if (precisaR) await carregarCategorias('R');
            if (precisaD) await carregarCategorias('D');
          } catch (err) { setErro(`Categorias do Omie: ${msgErro(err)}`); }
        })();
      }
      setTimeout(() => resultadoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      // Atualiza só a linha desta conta na revisão, sem consultar as outras de novo.
      setResumo((r) => r && r.map((x) => (x.conta !== c ? x : {
        ...x, omie_atual: data.saldos.omie_atual, dif: data.saldos.dif,
        so_planilha: data.so_planilha.length, so_omie: data.so_omie.length,
        nao_conciliados: [...data.so_omie, ...data.pareados.map((p) => p.omie)].filter((m) => m.situacao !== 'Conciliado').length,
        status: data.saldos.dif === 0 ? 'ok' : 'diferente',
      })));
    } catch (err) { setErro(msgErro(err)); } finally { setOcupado(''); }
  };
  const comparar = () => compararConta(conta, abaAtual);
  const abrirConta = (c) => {
    setConta(c); setResultado(null); setAviso(''); setFeitos({ lancados: {}, excluidos: {} });
    if (temDePara(c)) compararConta(c, abaAtual);
  };

  // O Omie recusa chamadas simultâneas (erro 8020): uma lista por vez, e a mesma lista só uma vez.
  const categoriasRef = useRef({ R: null, D: null, emCurso: null });
  const carregarCategorias = async (tipo) => {
    if (categoriasRef.current[tipo]) return categoriasRef.current[tipo];
    if (categoriasRef.current.emCurso) await categoriasRef.current.emCurso.catch(() => {});
    if (categoriasRef.current[tipo]) return categoriasRef.current[tipo];
    const pedido = axios.get(URL, { params: { acao: 'categorias', tipo }, headers: headers(), timeout: 120000 })
      .then(({ data }) => {
        categoriasRef.current[tipo] = data.categorias;
        setCategorias((c) => ({ ...c, [tipo]: data.categorias }));
        return data.categorias;
      })
      .finally(() => { categoriasRef.current.emCurso = null; });
    categoriasRef.current.emCurso = pedido;
    return pedido;
  };

  // Pré-seleção da categoria do Omie pela semelhança de nome com a categoria da planilha.
  // Ordem: igual → mesmo código numérico ("3.2.6 …") → mais palavras em comum (ex.:
  // "Reembolsos de Mensalidades" acha "Reembolsos"). Sem nada parecido, fica vazio.
  const STOP = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'em', 'para', 'com', 'a', 'o', 'as', 'os', 'via', 'por']);
  const semCodigo = (s) => norm(s).replace(/^[\d.]+\s*/, '').replace(/[^a-z0-9 ]/g, ' ').trim();
  const tokens = (s) => semCodigo(s).split(/\s+/).filter((t) => t.length >= 3 && !STOP.has(t));
  const preSelecionar = (lista, categoriaPlanilha) => {
    const alvo = norm(categoriaPlanilha);
    if (!alvo || !lista?.length) return '';
    const exata = lista.find((c) => norm(c.descricao) === alvo);
    if (exata) return exata.codigo;
    const tp = tokens(categoriaPlanilha);
    let melhor = null, nota = 0;
    if (tp.length) {
      lista.forEach((c) => {
        const tc = tokens(c.descricao);
        if (!tc.length) return;
        const comuns = tp.filter((t) => tc.some((u) => u === t || u.startsWith(t.slice(0, 5)) || t.startsWith(u.slice(0, 5)))).length;
        const n = comuns / Math.max(tp.length, tc.length) + (semCodigo(c.descricao).includes(semCodigo(categoriaPlanilha)) || semCodigo(categoriaPlanilha).includes(semCodigo(c.descricao)) ? 0.5 : 0);
        if (n > nota) { nota = n; melhor = c; }
      });
    }
    if (nota >= 0.5 && melhor) return melhor.codigo;            // nome parecido vence o código
    const codigo = (alvo.match(/^[\d.]+/) || [''])[0];
    const porCodigo = codigo && lista.find((c) => norm(c.descricao).startsWith(codigo + ' '));
    if (porCodigo) return porCodigo.codigo;
    return nota >= 0.34 && melhor ? melhor.codigo : '';
  };

  // Trava da transferência: a outra conta já tem este valor no Omie? Se sim, lançar a
  // transferência duplicaria lá (a outra ponta já foi lançada com outra origem/forma).
  // Devolve {estado: 'ok' | 'duplica' | 'sem_planilha' | 'sem_conta' | 'erro', outra, detalhe}.
  const checarContrapartida = async (linha) => {
    const p = partesTransf(linha.descricao);
    if (!p || !abaAtual) return null;
    const outraNome = mesmaConta(p[0], linha.conta) ? p[1] : p[0];
    const outra = (planilha?.contas || []).find((c) => mesmaConta(outraNome, c)) || outraNome;
    if (!temDePara(outra)) return { estado: 'sem_conta', outra, detalhe: `A conta "${outra}" não existe no Omie. A transferência não pode ser criada.` };
    if (contrapartidaFalta(linha)) return { estado: 'sem_planilha', outra, detalhe: `A planilha da conta "${outra}" não tem a contrapartida (${brl(-linha.valor)}). Confira o extrato das duas contas.` };
    try {
      const { data } = await axios.post(URL, {
        acao: 'comparar', conta: outra, ano: abaAtual.ano, mes: abaAtual.mes,
        linhas: abaAtual.linhas.filter((l) => l.conta === outra), saldos: abaAtual.saldos[outra] || {},
      }, { headers: headers(), timeout: 120000 });
      const alvo = Math.round(-linha.valor * 100) / 100;
      const faltaLa = data.so_planilha.some((l) => Math.abs(l.valor - alvo) < 0.005);
      if (faltaLa) return { estado: 'ok', outra, detalhe: `A conta "${outra}" também está sem este valor no Omie: a transferência acerta as duas contas de uma vez.` };
      const jaTem = data.pareados.find((x) => Math.abs(x.omie.valor - alvo) < 0.005);
      return { estado: 'duplica', outra,
        detalhe: `A conta "${outra}" já tem ${brl(alvo)} no Omie${jaTem ? ` (cód. ${jaTem.omie.nCodLancamento}, ${jaTem.omie.origem})` : ''} e está batendo. Lançar a transferência criaria uma duplicidade lá. O certo é abrir "${outra}", excluir esse lançamento e então lançar a transferência por aqui, ou lançar só o lado de "${linha.conta}" como lançamento comum.` };
    } catch (err) {
      return { estado: 'erro', outra, detalhe: `Não deu para conferir a conta "${outra}" no Omie: ${msgErro(err)}` };
    }
  };

  const abrirLancar = async (linha, excluir = null) => {
    setErro('');
    const transf = ehTransf(linha.descricao);
    let lista = [];
    if (!transf) {
      try { lista = await carregarCategorias(linha.valor >= 0 ? 'R' : 'D'); } catch (err) { setErro(msgErro(err)); return; }
    }
    const categoria = transf ? '' : (catLinha[linha.idx] || preSelecionar(lista, chavePre(linha)));
    setModal({ tipo: excluir ? 'refazer' : 'lancar', linha, excluir, transf, lista, categoria, checagem: transf ? { estado: 'checando' } : null, forcar: false });
    if (transf) {
      const checagem = await checarContrapartida(linha);
      setModal((m) => (m && m.linha === linha ? { ...m, checagem } : m));
    }
  };

  const executar = async (corpo) => {
    setOcupado('acao'); setErro(''); setAviso('');
    try {
      const { data } = await axios.post(URL, corpo, { headers: headers(), timeout: 120000 });
      let msg = '';
      let ok = true;
      if (corpo.acao === 'lancar') {
        msg = `Lançado no Omie com o código ${data.nCodLanc}.`;
        setFeitos((f) => ({ ...f, lancados: { ...f.lancados, [corpo.linha.idx]: data.nCodLanc } }));
      }
      if (corpo.acao === 'excluir') {
        msg = `Lançamento ${data.excluido} excluído do Omie.`;
        setFeitos((f) => ({ ...f, excluidos: { ...f.excluidos, [data.excluido]: true } }));
      }
      if (corpo.acao === 'refazer') {
        ok = !!data.excluido;
        msg = data.excluido
          ? `Lançado ${data.lancado} e excluído ${data.excluido}.`
          : `Lançado ${data.lancado}, mas a exclusão do antigo falhou (${data.erro}). Ficou duplicado: exclua o antigo pelo botão Excluir.`;
        setFeitos((f) => ({
          lancados: { ...f.lancados, [corpo.lancar.linha.idx]: data.lancado },
          excluidos: data.excluido ? { ...f.excluidos, [data.excluido]: true } : f.excluidos,
        }));
      }
      setModal(data.perna_restante ? { tipo: 'perna', perna: data.perna_restante } : null);
      setAviso(msg);
      if (ok) notify.sucesso(msg); else notify.aviso(msg);
      await comparar();
    } catch (err) { setErro(msgErro(err)); notify.erro(msgErro(err)); } finally { setOcupado(''); }
  };

  // Exclusão em lote: a tela manda UM código por chamada, em sequência; o backend continua
  // recusando lista. Para no primeiro erro e mostra o que foi e o que não foi.
  const excluirLote = async () => {
    const itens = (resultado?.so_omie || []).filter((m) => selecionados.includes(m.nCodLancamento) && !feitos.excluidos[m.nCodLancamento]);
    if (!itens.length) return;
    setOcupado('acao'); setErro(''); setAviso('');
    const prog = { itens, total: itens.length, feitos: 0, atual: null, erros: [], pernas: [] };
    setLote({ ...prog });
    for (const m of itens) {
      prog.atual = m.nCodLancamento; setLote({ ...prog });
      try {
        const { data } = await axios.post(URL, { acao: 'excluir', nCodLanc: m.nCodLancamento, conta, valor: m.valor, data: m.data }, { headers: headers(), timeout: 120000 });
        prog.feitos += 1;
        setFeitos((f) => ({ ...f, excluidos: { ...f.excluidos, [data.excluido]: true } }));
        if (data.perna_restante) prog.pernas.push(data.perna_restante);
      } catch (err) {
        prog.erros.push({ cod: m.nCodLancamento, erro: msgErro(err) });
        break;                                   // não segue depois de uma falha
      }
    }
    prog.atual = null; setLote({ ...prog });
    const msg = `${prog.feitos} de ${prog.total} excluído(s).` + (prog.erros.length ? ` Parou no código ${prog.erros[0].cod}: ${prog.erros[0].erro}` : '');
    setAviso(msg);
    if (prog.erros.length) notify.erro(msg); else notify.sucesso(msg);
    setSelecionados([]);
    setOcupado('');
    await compararConta(conta, abaAtual);
  };

  const saldos = resultado?.saldos;

  // ── consistência da planilha: quando ela mesma não fecha, o ajuste não é no Omie, é no extrato ──
  const abaAnterior = useMemo(() => {
    if (!planilha || !abaAtual) return null;
    const alvo = abaAtual.mes === 1 ? { ano: abaAtual.ano - 1, mes: 12 } : { ano: abaAtual.ano, mes: abaAtual.mes - 1 };
    return planilha.abas.find((a) => a.ano === alvo.ano && a.mes === alvo.mes) || null;
  }, [planilha, abaAtual]);

  // Nomes usados dentro de "Transferência (X > Y)" que não batem com o Local Caixa.
  const APELIDOS = { finops: 'brendi finops', d0: 'mp d0', d30: 'mp d30', rinne: 'rinne (celcoin)' };
  const mesmaConta = (nome, local) => { const n = norm(nome); return n === norm(local) || APELIDOS[n] === norm(local); };
  const partesTransf = (d) => { const m = /transfer[eê]ncia\s*\(\s*(.+?)\s*>\s*(.+?)\s*\)/i.exec(d || ''); return m ? [m[1], m[2]] : null; };

  // Transferência de uma linha tem a contrapartida (valor oposto, outra conta) no mesmo mês da planilha?
  const contrapartidaFalta = (l) => {
    const p = partesTransf(l.descricao);
    if (!p || !abaAtual) return null;
    const outra = mesmaConta(p[0], l.conta) ? p[1] : p[0];
    const existe = abaAtual.linhas.some((x) => x.conta !== l.conta && mesmaConta(outra, x.conta) && Math.abs(x.valor + l.valor) < 0.005);
    return existe ? null : outra;
  };

  const avisosPlanilha = useMemo(() => {
    if (!abaAtual || !conta) return [];
    const out = [];
    const sal = abaAtual.saldos[conta];
    const ant = abaAnterior?.saldos?.[conta];
    if (sal && ant && ant.final != null && sal.inicial != null && Math.abs(sal.inicial - ant.final) > 0.005) {
      out.push(`A planilha abre ${aba} com ${brl(sal.inicial)}, mas fechou ${abaAnterior.nome} com ${brl(ant.final)} (diferença ${brl(sal.inicial - ant.final)}). O saldo inicial foi digitado à mão. Confira o extrato do banco antes de ajustar no Omie.`);
    }
    if (saldos && saldos.plan_ini != null && saldos.omie_ant != null && Math.abs(saldos.plan_ini - saldos.omie_ant) > 0.005) {
      out.push(`O saldo inicial do mês na planilha (${brl(saldos.plan_ini)}) não é o mesmo do Omie (${brl(saldos.omie_ant)}). A diferença vem de antes de ${aba}: confira o extrato e o mês anterior antes de mexer neste.`);
    }
    abaAtual.linhas.filter((l) => l.conta === conta).forEach((l) => {
      const outra = contrapartidaFalta(l);
      if (outra) out.push(`${dataBr(l.data)} · ${l.descricao} (${brl(l.valor)}): a conta "${outra}" não tem a contrapartida na planilha. Uma das duas contas está errada. Confira o extrato das duas antes de lançar.`);
    });
    return out;
  }, [abaAtual, abaAnterior, conta, saldos]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Para a revisão do mês: abertura da planilha ≠ fechamento do mês anterior, por conta.
  const saltoAbertura = (c) => {
    const sal = abaAtual?.saldos?.[c]; const ant = abaAnterior?.saldos?.[c];
    if (!sal || !ant || sal.inicial == null || ant.final == null) return null;
    const d = Math.round((sal.inicial - ant.final) * 100) / 100;
    return Math.abs(d) > 0.005 ? d : null;
  };

  // Categoria efetiva de uma linha "Só na planilha": a escolhida no select, senão a pré-seleção.
  const categoriaDaLinha = (l) => {
    if (ehTransf(l.descricao)) return '';
    const lista = categorias[l.valor >= 0 ? 'R' : 'D'];
    return catLinha[l.idx] ?? (lista ? preSelecionar(lista, chavePre(l)) : '');
  };
  // Linha pode ir no lote: ainda não lançada e (transferência ou com categoria definida).
  const lancavel = (l) => !feitos.lancados[l.idx] && (ehTransf(l.descricao) || !!categoriaDaLinha(l));
  const lancaveis = linhasSoPlanilha.filter(lancavel).map((l) => l.idx);
  const todosLancar = lancaveis.length > 0 && lancaveis.every((i) => selLancar.includes(i));

  // Lançamento em lote: UM por chamada, em sequência; para no primeiro erro.
  const lancarLote = async () => {
    const itens = linhasSoPlanilha.filter((l) => selLancar.includes(l.idx) && lancavel(l));
    if (!itens.length) return;
    setOcupado('acao'); setErro(''); setAviso('');
    const prog = { tipo: 'lancar', itens, total: itens.length, feitos: 0, atual: null, erros: [], pernas: [] };
    setLote({ ...prog });
    for (const l of itens) {
      prog.atual = l.idx; setLote({ ...prog });
      try {
        const { data } = await axios.post(URL, { acao: 'lancar', conta, linha: l, categoria: categoriaDaLinha(l) }, { headers: headers(), timeout: 120000 });
        prog.feitos += 1;
        setFeitos((f) => ({ ...f, lancados: { ...f.lancados, [l.idx]: data.nCodLanc } }));
      } catch (err) {
        prog.erros.push({ cod: l.idx, erro: msgErro(err) });
        break;
      }
    }
    prog.atual = null; setLote({ ...prog });
    const msg = `${prog.feitos} de ${prog.total} lançado(s) no Omie.` + (prog.erros.length ? ` Parou na linha de ${dataBr(itens.find((l) => l.idx === prog.erros[0].cod)?.data)}: ${prog.erros[0].erro}` : '');
    setAviso(msg);
    if (prog.erros.length) notify.erro(msg); else notify.sucesso(msg);
    setSelLancar([]);
    setOcupado('');
    await compararConta(conta, abaAtual);
  };

  const excluiveis = (resultado?.so_omie || []).filter((m) => !feitos.excluidos[m.nCodLancamento]).map((m) => m.nCodLancamento);
  const todosMarcados = excluiveis.length > 0 && excluiveis.every((c) => selecionados.includes(c));
  const naoConciliados = resultado ? resultado.pareados.filter((p) => p.omie.situacao !== 'Conciliado').length : 0;

  return (
    <div className="painel-op"><div className="container-fluid mt-4 shadow p-4 rounded bg-white">
      <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center"><i className="fas fa-scale-balanced header-icon me-2" aria-hidden="true"></i>
          <h2 className="titulo-pagina m-0">BPO Financeiro — Conciliação Omie</h2></div>
        <div className="gr-toolbar">
          <select className="form-select form-select-sm" style={{ width: 160 }} value={aba} onChange={(e) => { setAba(e.target.value); setConta(''); setResultado(null); setAviso(''); }} disabled={!planilha} aria-label="Mês">
            <option value="">{planilha ? 'Mês…' : 'Suba a planilha'}</option>
            {(planilha?.abas || []).map((a) => <option key={a.nome} value={a.nome}>{a.nome}</option>)}
          </select>
          <select className="form-select form-select-sm" style={{ width: 230 }} value={conta} onChange={(e) => abrirConta(e.target.value)} disabled={!abaAtual} aria-label="Conta">
            <option value="">Conta…</option>
            {(planilha?.contas || []).map((c) => <option key={c} value={c}>{c}{temDePara(c) ? '' : ' (sem conta no Omie)'}</option>)}
          </select>
          <button type="button" className="gr-btn" onClick={comparar} disabled={!!ocupado || !abaAtual || !conta || !temDePara(conta)}>
            <i className={`fas ${ocupado === 'comparar' ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
          </button>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <input type="file" accept=".xlsx" ref={arquivoRef} onChange={importar} style={{ display: 'none' }} />
        <button type="button" className="btn btn-sm btn-primary" onClick={() => arquivoRef.current?.click()} disabled={!!ocupado}>
          {ocupado === 'planilha'
            ? <><span className="spinner-border spinner-border-sm me-2"></span>Lendo…</>
            : <><i className="fas fa-file-excel me-2"></i>Subir planilha (xlsx)</>}
        </button>
        <span className="small text-muted">
          {planilha ? `${planilha.nome} · ${planilha.abas.length} mês(es)` : 'Planilha de fluxo do cliente, uma aba por mês. Fica só nesta tela; fechar o navegador apaga.'}
        </span>
        <span className="small text-muted ms-auto">Cliente: Brendi</span>
      </div>

      {erro && <div className="alert alert-danger py-2 small">{erro}</div>}
      {aviso && <div className="alert alert-success py-2 small">{aviso}</div>}
      {conta && !temDePara(conta) && (
        <div className="alert alert-warning py-2 small">A conta "{conta}" não tem correspondência no Omie. Crie a conta no Omie e peça para incluir no de-para.</div>
      )}

      {/* ── revisão do mês: todas as contas ── */}
      {abaAtual && (
        <div className="mb-4">
          <div className="d-flex align-items-center gap-2 mb-2">
            <h6 className="fw-bold m-0"><i className="fas fa-list-check me-2" style={{ color: '#00b5cc' }}></i>Revisão de {aba}: saldo final planilha × Omie</h6>
            {ocupado === 'resumo' && <span className="small text-muted"><span className="spinner-border spinner-border-sm me-1"></span>Consultando o Omie, uma conta por vez…</span>}
            {resumo && ocupado !== 'resumo' && (
              <span className="small text-muted">
                {resumo.filter((x) => x.status === 'ok').length} batem · {resumo.filter((x) => x.status === 'diferente').length} com diferença · {resumo.filter((x) => x.status === 'sem_conta').length} sem conta no Omie
              </span>
            )}
            <button type="button" className="btn btn-sm btn-outline-secondary ms-auto" onClick={() => revisar(abaAtual)} disabled={!!ocupado}>
              <i className="fas fa-rotate me-1"></i>Revisar de novo
            </button>
          </div>
          {resumo && (
            <table className="table table-sm table-hover small align-middle mb-0">
              <thead>
                <tr><th>Conta</th><th className="text-end">Planilha fim</th><th className="text-end">Omie atual</th><th className="text-end">Diferença</th>
                  <th className="text-center">Só planilha</th><th className="text-center">Só Omie</th><th className="text-center">Não conciliados</th><th>Status</th><th className="text-center">Análise</th><th style={{ minWidth: 220 }}>Obs.</th></tr>
              </thead>
              <tbody>
                {resumo.map((x) => (
                  <tr key={x.conta} style={{ cursor: x.status === 'sem_conta' ? 'default' : 'pointer' }}
                    className={x.conta === conta ? 'table-active' : ''}
                    onClick={() => x.status !== 'sem_conta' && abrirConta(x.conta)} title={x.status === 'sem_conta' ? '' : 'Abrir a conta'}>
                    <td className="fw-semibold">
                      {x.conta}
                      {saltoAbertura(x.conta) != null && (
                        <i className="fas fa-triangle-exclamation text-warning ms-2" title={`A planilha abre ${aba} ${brl(saltoAbertura(x.conta))} diferente do fechamento do mês anterior. Confira o extrato.`}></i>
                      )}
                    </td>
                    <td className="text-end">{brl(x.plan_fim)}</td>
                    <td className="text-end">{brl(x.omie_atual)}</td>
                    <td className={`text-end fw-bold ${x.dif === 0 ? 'text-success' : x.dif == null ? 'text-muted' : 'text-danger'}`}>{brl(x.dif)}</td>
                    <td className={`text-center ${x.so_planilha ? 'text-warning fw-bold' : 'text-muted'}`}>{x.so_planilha}</td>
                    <td className={`text-center ${x.so_omie ? 'text-danger fw-bold' : 'text-muted'}`}>{x.so_omie}</td>
                    <td className={`text-center ${x.nao_conciliados ? 'text-warning fw-bold' : 'text-muted'}`}>{x.nao_conciliados}</td>
                    <td>
                      {x.status === 'ok' && <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle">Bate</span>}
                      {x.status === 'diferente' && <span className="badge bg-danger-subtle text-danger-emphasis border border-danger-subtle">Não bate</span>}
                      {x.status === 'sem_conta' && <span className="badge bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle">Sem conta no Omie</span>}
                      {x.status === 'erro' && <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle" title={x.erro || ''}>Erro no Omie</span>}
                    </td>
                    <td className="text-center" onClick={(e) => e.stopPropagation()}>
                      {x.status !== 'sem_conta' && (
                        <button className="btn btn-sm btn-outline-primary py-0" onClick={() => analisar(x.conta)} disabled={ocupado === 'analise' || ocupado === 'resumo'}
                          title="Percorrer os meses desde o início da planilha e apontar onde a diferença começa e a causa provável">
                          <i className="fas fa-magnifying-glass-chart me-1"></i>Analisar
                        </button>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="text" className="form-control form-control-sm" value={obsDe(x.conta)} placeholder="Anotar…"
                        onChange={(e) => setObsSujas((p) => ({ ...p, [x.conta]: e.target.value }))}
                        onBlur={(e) => gravarObs(x.conta, e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                        title={obs[x.conta] ? `Gravado por ${obs[x.conta].por} em ${obs[x.conta].em}` : 'Observação deste mês para esta conta (grava ao sair do campo)'}
                        aria-label={`Observação ${x.conta}`} />
                    </td>
                  </tr>))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {resultado && (<div ref={resultadoRef}>
        <h5 className="fw-bold mt-2 mb-2" style={{ color: '#00205b' }}><i className="fas fa-building-columns me-2" style={{ color: '#F1BE48' }}></i>{conta} · {aba}</h5>
        {avisosPlanilha.length > 0 && (
          <div className="alert alert-warning py-2 small mb-3">
            <div className="fw-bold mb-1"><i className="fas fa-triangle-exclamation me-2"></i>A planilha do cliente não fecha nesta conta. Antes de ajustar no Omie, confira o extrato do banco.</div>
            <ul className="mb-0 ps-3">{avisosPlanilha.map((t, i) => <li key={i}>{t}</li>)}</ul>
          </div>
        )}
        {saldos && (
          <div className="d-flex flex-wrap gap-3 border rounded px-3 py-2 mb-3 small" style={{ background: '#f8f9fa' }}>
            <span>Planilha início: <b>{brl(saldos.plan_ini)}</b></span>
            <span>Planilha fim: <b>{brl(saldos.plan_fim)}</b></span>
            <span>Omie anterior: <b>{brl(saldos.omie_ant)}</b></span>
            <span>Omie atual: <b>{brl(saldos.omie_atual)}</b></span>
            <span className={saldos.dif === 0 ? 'text-success fw-bold' : 'text-danger fw-bold'}>Diferença (Omie − planilha): {brl(saldos.dif)}</span>
          </div>
        )}

        <div className="d-flex align-items-center gap-2 mt-3 mb-1">
          <h6 className="fw-bold m-0"><i className="fas fa-table-list me-2" style={{ color: '#00b5cc' }}></i>Linhas da planilha ({linhasPlanilha.length})</h6>
          <div className="btn-group btn-group-sm ms-2" role="group" aria-label="Filtro das linhas">
            <button type="button" className={`btn ${filtroPlan === 'faltam' ? 'btn-warning' : 'btn-outline-secondary'}`} onClick={() => setFiltroPlan('faltam')}>Faltam no Omie ({linhasSoPlanilha.length})</button>
            <button type="button" className={`btn ${filtroPlan === 'omie' ? 'btn-success' : 'btn-outline-secondary'}`} onClick={() => setFiltroPlan('omie')}>Já no Omie ({resultado.pareados.length})</button>
            <button type="button" className={`btn ${filtroPlan === 'todas' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setFiltroPlan('todas')}>Todas</button>
          </div>
          {naoConciliados > 0 && <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle" title="Estão no Omie, mas sem a marcação de conciliado">{naoConciliados} sem marcação de conciliado no Omie</span>}
          {selLancar.length > 0 && (
            <button type="button" className="btn btn-sm btn-primary ms-auto" disabled={ocupado === 'acao'} onClick={() => setModal({ tipo: 'lancar_lote' })}>
              <i className="fas fa-plus me-1"></i>Lançar selecionados ({selLancar.length})
            </button>
          )}
        </div>
        <table className="table table-sm table-hover small align-middle">
          <thead><tr>
            <th style={{ width: 28 }}>
              <input type="checkbox" className="form-check-input" aria-label="Marcar todos" checked={todosLancar} disabled={!lancaveis.length || ocupado === 'acao'}
                onChange={(e) => setSelLancar(e.target.checked ? lancaveis : [])} title="Marca as linhas que já têm categoria (ou são transferência)" />
            </th>
            <th>Data</th><th className="text-end">Valor</th><th>Descrição</th><th style={{ minWidth: 260 }}>Categoria no Omie</th><th></th></tr></thead>
          <tbody>
            {linhasFiltradas.length === 0 && <tr><td colSpan="6" className="text-muted">{filtroPlan === 'faltam' ? 'Nada faltando no Omie.' : 'Nenhuma linha.'}</td></tr>}
            {linhasFiltradas.filter((x) => x.par).map(({ l, par }) => (
              <tr key={`p${l.idx}`} className={par.situacao !== 'Conciliado' ? 'table-warning' : ''}>
                <td></td>
                <td>{dataBr(l.data)}</td>
                <td className={`text-end ${l.valor < 0 ? 'text-danger' : 'text-success'}`}>{brl(l.valor)}</td>
                <td>{l.descricao}</td>
                <td>
                  <div>{par.categoria || <span className="text-muted">{par.origem}</span>}</div>
                  <div className="text-muted" style={{ fontSize: '.75rem' }}>planilha: {classifPlanilha(l) || <i>sem classificação na planilha</i>}</div>
                </td>
                <td className="text-end text-nowrap">
                  <span className={`badge border ${par.situacao === 'Conciliado' ? 'bg-success-subtle text-success-emphasis border-success-subtle' : 'bg-warning-subtle text-warning-emphasis border-warning-subtle'}`}
                    title={`Omie: ${dataBr(par.data)} · ${par.cliente || par.obs || par.origem}`}>
                    <i className="fas fa-check me-1"></i>No Omie · cód. {par.nCodLancamento}{par.situacao !== 'Conciliado' ? ' · não conciliado' : ''}
                  </span>
                </td>
              </tr>))}
            {linhasFiltradas.filter((x) => !x.par).map(({ l }) => {
              const transf = ehTransf(l.descricao);
              const lista = categorias[l.valor >= 0 ? 'R' : 'D'];
              const escolhida = catLinha[l.idx] ?? (lista ? preSelecionar(lista, chavePre(l)) : '');
              return (
              <tr key={l.idx} className={selLancar.includes(l.idx) ? 'table-primary' : ''}>
                <td>
                  {!feitos.lancados[l.idx] && (
                    <input type="checkbox" className="form-check-input" aria-label={`Marcar linha ${l.idx}`} checked={selLancar.includes(l.idx)}
                      disabled={ocupado === 'acao' || !lancavel(l)} title={lancavel(l) ? '' : 'Escolha a categoria antes de marcar'}
                      onChange={(e) => setSelLancar((s) => (e.target.checked ? [...s, l.idx] : s.filter((i) => i !== l.idx)))} />
                  )}
                </td>
                <td>{dataBr(l.data)}</td>
                <td className={`text-end ${l.valor < 0 ? 'text-danger' : 'text-success'}`}>{brl(l.valor)}</td>
                <td>{l.descricao}</td>
                <td>
                  {transf
                    ? <span className="text-muted">transferência entre contas</span>
                    : !lista
                      ? <span className="text-muted"><span className="spinner-border spinner-border-sm me-1"></span>categorias…</span>
                      : <select className="form-select form-select-sm" value={escolhida} onChange={(e) => setCatLinha((m) => ({ ...m, [l.idx]: e.target.value }))} title={classifPlanilha(l) ? `Na planilha: ${classifPlanilha(l)}` : ''}>
                          <option value="">Escolha…</option>
                          {lista.map((c) => <option key={c.codigo} value={c.codigo}>{c.descricao}</option>)}
                        </select>}
                  {!transf && (
                    <div className="text-muted" style={{ fontSize: '.75rem' }}>
                      planilha: {classifPlanilha(l) || <i>sem classificação na planilha</i>}
                    </div>
                  )}
                </td>
                <td className="text-end text-nowrap">
                  {feitos.lancados[l.idx]
                    ? <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle"><i className="fas fa-check me-1"></i>Lançado · cód. {feitos.lancados[l.idx]}</span>
                    : <button className="btn btn-sm btn-outline-primary" disabled={ocupado === 'acao'} onClick={() => abrirLancar(l)}><i className="fas fa-plus me-1"></i>Lançar</button>}
                </td>
              </tr>);
            })}
          </tbody>
        </table>

        <div className="d-flex align-items-center gap-2 mt-4 mb-1">
          <h6 className="fw-bold m-0"><i className="fas fa-file-circle-minus me-2 text-danger"></i>Só no Omie ({resultado.so_omie.length})</h6>
          {selecionados.length > 0 && (
            <button type="button" className="btn btn-sm btn-danger ms-auto" disabled={ocupado === 'acao'} onClick={() => setModal({ tipo: 'excluir_lote' })}>
              <i className="fas fa-trash me-1"></i>Excluir selecionados ({selecionados.length})
            </button>
          )}
        </div>
        <table className="table table-sm table-hover small align-middle">
          <thead><tr>
            <th style={{ width: 28 }}>
              <input type="checkbox" className="form-check-input" aria-label="Marcar todos" checked={todosMarcados} disabled={!excluiveis.length || ocupado === 'acao'}
                onChange={(e) => setSelecionados(e.target.checked ? excluiveis : [])} />
            </th>
            <th>Data</th><th className="text-end">Valor</th><th>Cliente / Obs.</th><th>Origem</th><th>Situação</th><th>Cód.</th><th></th></tr></thead>
          <tbody>
            {resultado.so_omie.length === 0 && <tr><td colSpan="8" className="text-muted">Nada sobrando no Omie.</td></tr>}
            {resultado.so_omie.map((m) => (
              <tr key={m.nCodLancamento} className={selecionados.includes(m.nCodLancamento) ? 'table-danger' : ''}>
                <td>
                  {!feitos.excluidos[m.nCodLancamento] && (
                    <input type="checkbox" className="form-check-input" aria-label={`Marcar ${m.nCodLancamento}`} checked={selecionados.includes(m.nCodLancamento)} disabled={ocupado === 'acao'}
                      onChange={(e) => setSelecionados((s) => (e.target.checked ? [...s, m.nCodLancamento] : s.filter((c) => c !== m.nCodLancamento)))} />
                  )}
                </td>
                <td>{dataBr(m.data)}</td>
                <td className={`text-end ${m.valor < 0 ? 'text-danger' : 'text-success'}`}>{brl(m.valor)}</td>
                <td>{m.cliente || m.obs}</td><td className="text-muted">{m.origem}</td>
                <td><span className={`badge ${m.situacao === 'Conciliado' ? 'bg-success-subtle text-success-emphasis' : 'bg-warning-subtle text-warning-emphasis'}`}>{m.situacao || '-'}</span></td>
                <td className="text-muted">{m.nCodLancamento}</td>
                <td className="text-end text-nowrap">
                  {feitos.excluidos[m.nCodLancamento]
                    ? <span className="badge bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle"><i className="fas fa-check me-1"></i>Excluído do Omie</span>
                    : <>
                      <button className="btn btn-sm btn-outline-secondary me-1" disabled={ocupado === 'acao' || linhasSoPlanilha.length === 0} title="Substituir por uma linha da planilha" onClick={() => setModal({ tipo: 'escolher', mov: m })}>
                        <i className="fas fa-arrows-rotate me-1"></i>Refazer
                      </button>
                      <button className="btn btn-sm btn-outline-danger" disabled={ocupado === 'acao'} onClick={() => setModal({ tipo: 'excluir', mov: m })}>
                        <i className="fas fa-trash me-1"></i>Excluir
                      </button>
                    </>}
                </td>
              </tr>))}
          </tbody>
        </table>

      </div>)}

      {/* ── modal Lançar / Refazer (dados + categoria) ── */}
      {modal && (modal.tipo === 'lancar' || modal.tipo === 'refazer') && (
        <Modal titulo={modal.tipo === 'lancar' ? 'Lançar no Omie' : 'Refazer: lançar o novo e excluir o antigo'} onFechar={() => setModal(null)} ocupado={ocupado === 'acao'}
          rodape={<>
            <button className="btn btn-secondary me-auto" onClick={() => setModal(null)} disabled={ocupado === 'acao'}>Cancelar</button>
            <button className="btn btn-primary"
              disabled={ocupado === 'acao' || (!modal.transf && !modal.categoria) || (modal.modo === 'comum' && !modal.categoria)}
              onClick={() => executar(modal.tipo === 'lancar'
                ? { acao: 'lancar', conta, linha: modal.linha, categoria: modal.categoria, comum: modal.modo === 'comum' }
                : { acao: 'refazer',
                    excluir: { nCodLanc: modal.excluir.nCodLancamento, conta, valor: modal.excluir.valor, data: modal.excluir.data },
                    lancar: { conta, linha: modal.linha, categoria: modal.categoria, comum: modal.modo === 'comum' } })}>
              <i className={`fas ${ocupado === 'acao' ? 'fa-spinner fa-spin' : 'fa-check'} me-2`}></i>{modal.tipo === 'lancar' ? 'Lançar' : 'Refazer'}
            </button>
          </>}>
          <div className="small">
            <div className="mb-2"><b>Conta:</b> {conta} · <b>Data:</b> {dataBr(modal.linha.data)} · <b>Valor:</b> <span className={modal.linha.valor < 0 ? 'text-danger' : 'text-success'}>{brl(modal.linha.valor)}</span></div>
            <div className="mb-2"><b>Descrição:</b> {modal.linha.descricao}</div>
            {modal.transf
              ? <>
                  <div className="alert alert-info py-2">Vai entrar como <b>transferência entre contas</b> no Omie, nas duas contas da descrição. Sem categoria.</div>
                  {modal.checagem?.estado === 'checando' && (
                    <div className="alert alert-light border py-2"><span className="spinner-border spinner-border-sm me-2"></span>Conferindo a outra conta no Omie antes de liberar…</div>
                  )}
                  {modal.checagem?.estado === 'ok' && (
                    <div className="alert alert-success py-2"><i className="fas fa-circle-check me-2"></i>{modal.checagem.detalhe}</div>
                  )}
                  {(modal.checagem?.estado === 'sem_planilha' || modal.checagem?.estado === 'erro') && (
                    <div className="alert alert-warning py-2"><i className="fas fa-triangle-exclamation me-2"></i>{modal.checagem.detalhe}</div>
                  )}
                  {(modal.checagem?.estado === 'duplica' || modal.checagem?.estado === 'sem_conta') && (
                    <div className="alert alert-danger py-2">
                      <div><i className="fas fa-triangle-exclamation me-2"></i><b>Atenção:</b> {modal.checagem.detalhe}</div>
                      {modal.checagem.estado === 'duplica' && (
                        <div className="mt-2">
                          <div className="form-check mb-1">
                            <input className="form-check-input" type="radio" name="modoTransf" id="modoComum" checked={modal.modo === 'comum'}
                              onChange={async () => {
                                let lista = modal.lista || [];
                                if (!lista.length) { try { lista = await carregarCategorias(modal.linha.valor >= 0 ? 'R' : 'D'); } catch (err) { setErro(msgErro(err)); } }
                                setModal((m) => ({ ...m, modo: 'comum', lista, categoria: m.categoria || preSelecionar(lista, chavePre(m.linha)) }));
                              }} />
                            <label className="form-check-label" htmlFor="modoComum"><b>Lançar só nesta conta</b>, como lançamento comum. A conta "{modal.checagem.outra}" fica como está. (recomendado)</label>
                          </div>
                          {modal.modo === 'comum' && (
                            <select className="form-select form-select-sm mb-2 ms-4" style={{ maxWidth: 420 }} value={modal.categoria} onChange={(e) => setModal({ ...modal, categoria: e.target.value })}>
                              <option value="">Categoria do Omie…</option>
                              {(modal.lista || []).map((c) => <option key={c.codigo} value={c.codigo}>{c.descricao}</option>)}
                            </select>
                          )}
                          <div className="form-check mb-0">
                            <input className="form-check-input" type="radio" name="modoTransf" id="modoForcar" checked={modal.modo === 'forcar'} onChange={() => setModal({ ...modal, modo: 'forcar' })} />
                            <label className="form-check-label" htmlFor="modoForcar">Lançar como transferência mesmo assim (vai duplicar em "{modal.checagem.outra}"). É o que acontece se você confirmar sem escolher nada.</label>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              : <div className="mb-2">
                  <label className="form-label mb-1"><b>Categoria do Omie</b> ({modal.linha.valor >= 0 ? 'receitas' : 'despesas'})</label>
                  <select className="form-select form-select-sm" value={modal.categoria} onChange={(e) => setModal({ ...modal, categoria: e.target.value })}>
                    <option value="">Escolha…</option>
                    {modal.lista.map((c) => <option key={c.codigo} value={c.codigo}>{c.descricao}</option>)}
                  </select>
                  <div className="text-muted mt-1">Na planilha: {classifPlanilha(modal.linha) || <i>sem classificação</i>}</div>
                </div>}
            {modal.excluir && (
              <div className="alert alert-warning py-2 mb-0">
                Depois de lançar, será excluído o lançamento <b>{modal.excluir.nCodLancamento}</b> ({dataBr(modal.excluir.data)}, {brl(modal.excluir.valor)}). Um lançamento só.
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* ── modal Excluir ── */}
      {modal && modal.tipo === 'excluir' && (
        <Modal titulo="Excluir lançamento do Omie" perigo onFechar={() => setModal(null)} ocupado={ocupado === 'acao'}
          rodape={<>
            <button className="btn btn-secondary me-auto" onClick={() => setModal(null)} disabled={ocupado === 'acao'}>Cancelar</button>
            <button className="btn btn-danger" disabled={ocupado === 'acao'}
              onClick={() => executar({ acao: 'excluir', nCodLanc: modal.mov.nCodLancamento, conta, valor: modal.mov.valor, data: modal.mov.data })}>
              <i className={`fas ${ocupado === 'acao' ? 'fa-spinner fa-spin' : 'fa-trash'} me-2`}></i>Excluir este lançamento
            </button>
          </>}>
          <div className="small">
            <div><b>Código:</b> {modal.mov.nCodLancamento} · <b>Conta:</b> {conta}</div>
            <div><b>Data:</b> {dataBr(modal.mov.data)} · <b>Valor:</b> {brl(modal.mov.valor)} · <b>Origem:</b> {modal.mov.origem}</div>
            <div><b>Cliente / Obs.:</b> {modal.mov.cliente || modal.mov.obs || '-'}</div>
            <div className="alert alert-warning py-2 mt-2 mb-0">
              O Saber confere no Omie conta, valor e data antes de excluir, e exclui <b>só este código</b>.
              {modal.mov.transferencia && ' É uma transferência: a outra perna será mostrada depois para você decidir.'}
            </div>
          </div>
        </Modal>
      )}

      {/* ── modal escolher linha da planilha para Refazer ── */}
      {modal && modal.tipo === 'escolher' && (
        <Modal titulo={`Refazer o lançamento ${modal.mov.nCodLancamento}: qual linha da planilha entra no lugar?`} onFechar={() => setModal(null)}
          rodape={<button className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>}>
          <table className="table table-sm table-hover small">
            <tbody>
              {linhasSoPlanilha.map((l) => (
                <tr key={l.idx} style={{ cursor: 'pointer' }} onClick={() => abrirLancar(l, modal.mov)}>
                  <td>{dataBr(l.data)}</td><td className="text-end">{brl(l.valor)}</td><td>{l.descricao}</td><td className="text-muted">{l.categoria}</td>
                </tr>))}
            </tbody>
          </table>
        </Modal>
      )}

      {/* ── modal lançar em lote (uma linha por chamada, em sequência) ── */}
      {modal && modal.tipo === 'lancar_lote' && (() => {
        const itens = lote?.itens || linhasSoPlanilha.filter((l) => selLancar.includes(l.idx) && lancavel(l));
        const rodando = !!lote && lote.atual != null;
        const terminou = !!lote && lote.atual == null;
        const nomeCat = (l) => {
          if (ehTransf(l.descricao)) return 'transferência entre contas';
          const lista = categorias[l.valor >= 0 ? 'R' : 'D'] || [];
          return lista.find((c) => c.codigo === categoriaDaLinha(l))?.descricao || categoriaDaLinha(l);
        };
        return (
          <Modal titulo={`Lançar ${itens.length} linha(s) no Omie`} onFechar={() => { if (!rodando) { setModal(null); setLote(null); } }} ocupado={rodando}
            rodape={<>
              {!terminou && <button className="btn btn-secondary me-auto" onClick={() => setModal(null)} disabled={rodando}>Cancelar</button>}
              {terminou && <button className="btn btn-secondary me-auto" onClick={() => { setModal(null); setLote(null); }}>Fechar</button>}
              {!terminou && (
                <button className="btn btn-primary" disabled={rodando || !itens.length} onClick={lancarLote}>
                  <i className={`fas ${rodando ? 'fa-spinner fa-spin' : 'fa-check'} me-2`}></i>{rodando ? `Lançando ${lote.feitos + 1} de ${lote.total}…` : `Lançar as ${itens.length}, uma por vez`}
                </button>
              )}
            </>}>
            <div className="small">
              <table className="table table-sm mb-2">
                <thead><tr><th>Data</th><th className="text-end">Valor</th><th>Descrição</th><th>Categoria no Omie</th><th></th></tr></thead>
                <tbody>
                  {itens.map((l) => (
                    <tr key={l.idx} className={lote?.erros?.some((e) => e.cod === l.idx) ? 'table-danger' : feitos.lancados[l.idx] ? 'table-success' : ''}>
                      <td>{dataBr(l.data)}</td><td className="text-end">{brl(l.valor)}</td><td>{l.descricao}</td><td className="text-muted">{nomeCat(l)}</td>
                      <td className="text-end">{lote?.atual === l.idx ? <span className="spinner-border spinner-border-sm"></span> : feitos.lancados[l.idx] ? <span className="text-muted">cód. {feitos.lancados[l.idx]}</span> : null}</td>
                    </tr>))}
                </tbody>
              </table>
              {!lote && (
                <div className="alert alert-info py-2 mb-0">
                  Cada linha vira uma chamada separada ao Omie, com a categoria mostrada acima. Se uma falhar, o lote para nela e as seguintes não são enviadas.
                </div>
              )}
              {terminou && (
                <div className={`alert ${lote.erros.length ? 'alert-danger' : 'alert-success'} py-2 mb-0`}>
                  {lote.feitos} de {lote.total} lançado(s).
                  {lote.erros.map((e) => <div key={e.cod}>Parou na linha de {dataBr(itens.find((l) => l.idx === e.cod)?.data)}: {e.erro}</div>)}
                </div>
              )}
            </div>
          </Modal>
        );
      })()}

      {/* ── modal excluir em lote (um código por chamada, em sequência) ── */}
      {modal && modal.tipo === 'excluir_lote' && (() => {
        const itens = lote?.itens || (resultado?.so_omie || []).filter((m) => selecionados.includes(m.nCodLancamento) && !feitos.excluidos[m.nCodLancamento]);
        const rodando = !!lote && lote.atual != null;
        const terminou = !!lote && lote.atual == null;
        return (
          <Modal titulo={`Excluir ${itens.length} lançamento(s) do Omie`} perigo onFechar={() => { if (!rodando) { setModal(null); setLote(null); } }} ocupado={rodando}
            rodape={<>
              {!terminou && <button className="btn btn-secondary me-auto" onClick={() => setModal(null)} disabled={rodando}>Cancelar</button>}
              {terminou && <button className="btn btn-secondary me-auto" onClick={() => { setModal(null); setLote(null); }}>Fechar</button>}
              {!terminou && (
                <button className="btn btn-danger" disabled={rodando || !itens.length} onClick={excluirLote}>
                  <i className={`fas ${rodando ? 'fa-spinner fa-spin' : 'fa-trash'} me-2`}></i>{rodando ? `Excluindo ${lote.feitos + 1} de ${lote.total}…` : `Excluir os ${itens.length}, um por vez`}
                </button>
              )}
            </>}>
            <div className="small">
              <table className="table table-sm mb-2">
                <tbody>
                  {itens.map((m) => (
                    <tr key={m.nCodLancamento} className={lote?.erros?.some((e) => e.cod === m.nCodLancamento) ? 'table-danger' : feitos.excluidos[m.nCodLancamento] ? 'table-success' : ''}>
                      <td>{dataBr(m.data)}</td><td className="text-end">{brl(m.valor)}</td><td>{m.cliente || m.obs}</td><td className="text-muted">{m.nCodLancamento}</td>
                      <td className="text-end">{lote?.atual === m.nCodLancamento && <span className="spinner-border spinner-border-sm"></span>}</td>
                    </tr>))}
                </tbody>
              </table>
              {!lote && (
                <div className="alert alert-warning py-2 mb-0">
                  Cada lançamento é conferido (conta, valor, data) e excluído numa chamada separada ao Omie. Se um falhar, o lote para nele e os seguintes não são tocados.
                  {itens.some((m) => m.transferencia) && ' Há transferências na lista: só a perna desta conta sai; as outras pernas aparecem no fim para você decidir.'}
                </div>
              )}
              {terminou && (
                <div className={`alert ${lote.erros.length ? 'alert-danger' : 'alert-success'} py-2 mb-0`}>
                  {lote.feitos} de {lote.total} excluído(s).
                  {lote.erros.map((e) => <div key={e.cod}>Parou no código {e.cod}: {e.erro}</div>)}
                  {lote.pernas.length > 0 && (
                    <div className="mt-1">Outras pernas de transferência que continuam no Omie: {lote.pernas.map((p) => `${p.conta} · cód. ${p.nCodLanc} (${brl(p.valor)})`).join('; ')}. Abra essas contas para excluir, se for o caso.</div>
                  )}
                </div>
              )}
            </div>
          </Modal>
        );
      })()}

      {/* ── modal perna restante da transferência ── */}
      {/* ── modal análise da diferença ── */}
      {modal && modal.tipo === 'analise' && (() => {
        const d = modal.dados;
        const mesmaDif = d && resumo
          ? resumo.filter((x) => x.conta !== modal.conta && x.dif != null && Math.abs(Math.abs(x.dif) - Math.abs(d.dif_final)) < 0.005 && Math.abs(d.dif_final) > 0.005)
          : [];
        return (
          <Modal titulo={`Análise da diferença: ${modal.conta}`} onFechar={() => setModal(null)} ocupado={ocupado === 'analise'}
            rodape={<>
              <button className="btn btn-secondary me-auto" onClick={() => setModal(null)} disabled={ocupado === 'analise'}>Fechar</button>
              {d && <button className="btn btn-outline-secondary" onClick={() => { setObsSujas((p) => ({ ...p, [modal.conta]: obsDaAnalise(d) })); gravarObs(modal.conta, obsDaAnalise(d)); notify.sucesso('Observação gravada'); }}>
                <i className="fas fa-pen me-2"></i>Usar como obs.
              </button>}
              {d && d.inicio && <button className="btn btn-primary" onClick={() => abrirMes(d.inicio.nome, modal.conta)}>
                <i className="fas fa-folder-open me-2"></i>Abrir {modal.conta} em {d.inicio.nome}
              </button>}
            </>}>
            {ocupado === 'analise' && <div className="text-muted small"><span className="spinner-border spinner-border-sm me-2"></span>Consultando o Omie mês a mês desde o início da planilha…</div>}
            {modal.erro && <div className="alert alert-danger py-2 small mb-0">{modal.erro}</div>}
            {d && (
              <div className="small">
                <div className={`alert py-2 ${d.inicio ? 'alert-warning' : 'alert-success'}`}>
                  <i className={`fas ${d.inicio ? 'fa-triangle-exclamation' : 'fa-circle-check'} me-2`}></i>{d.conclusao}
                </div>
                {mesmaDif.length > 0 && (
                  <div className="alert alert-info py-2">
                    <i className="fas fa-link me-2"></i>Mesma diferença ({brl(Math.abs(d.dif_final))}) em: <b>{mesmaDif.map((x) => x.conta).join(', ')}</b>. Pode ser ajuste feito na conta errada.
                  </div>
                )}
                {d.mudancas.map((mu) => (
                  <div key={mu.nome} className="border rounded p-2 mb-2">
                    <div className="fw-bold mb-1" style={{ color: '#00205b' }}>
                      {mu.nome}: diferença muda {brl(mu.variacao)} e passa a {brl(mu.dif_apos)}
                    </div>
                    {mu.causas.map((c, i) => (
                      <div key={i} className="mb-2">
                        <div><span className="badge bg-secondary-subtle text-secondary-emphasis border me-2">{ROTULO_CAUSA[c.tipo] || c.tipo}</span>{c.texto}</div>
                        {c.itens.length > 0 && (
                          <table className="table table-sm mb-0 mt-1" style={{ fontSize: '.8rem' }}>
                            <tbody>
                              {c.itens.map((it, j) => (
                                <tr key={j}>
                                  {it.conta && <td>{it.conta}</td>}
                                  <td>{dataBr(it.data)}</td>
                                  <td className="text-end">{it.plan_valor != null ? <>{brl(it.plan_valor)} <span className="text-muted">×</span> {brl(it.omie_valor)}</> : brl(it.valor)}</td>
                                  <td>{it.descricao || it.obs || '-'}</td>
                                  <td className="text-muted">{it.nCodLancamento ? `cód. ${it.nCodLancamento}` : ''}{it.situacao ? ` · ${it.situacao}` : ''}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
                <table className="table table-sm table-hover mb-0 mt-2" style={{ fontSize: '.8rem' }}>
                  <thead><tr><th>Mês</th><th className="text-end">Planilha abre</th><th className="text-end">Omie abre</th><th className="text-end">Planilha fecha</th><th className="text-end">Omie fecha</th><th className="text-end">Dif.</th><th className="text-center">Só plan.</th><th className="text-center">Só Omie</th></tr></thead>
                  <tbody>
                    {d.meses.map((m) => (
                      <tr key={m.nome} className={Math.abs(m.variacao) > 0.005 ? 'table-warning' : ''}>
                        <td>{m.nome}</td>
                        <td className="text-end">{brl(m.plan_ini)}</td><td className="text-end">{brl(m.omie_ant)}</td>
                        <td className="text-end">{brl(m.plan_fim)}</td><td className="text-end">{brl(m.omie_atual)}</td>
                        <td className={`text-end fw-bold ${m.dif === 0 ? 'text-success' : m.dif == null ? 'text-muted' : 'text-danger'}`}>{brl(m.dif)}</td>
                        <td className={`text-center ${m.so_planilha ? 'text-warning fw-bold' : 'text-muted'}`}>{m.so_planilha}</td>
                        <td className={`text-center ${m.so_omie ? 'text-danger fw-bold' : 'text-muted'}`}>{m.so_omie}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Modal>
        );
      })()}

      {modal && modal.tipo === 'perna' && (
        <Modal titulo="Transferência: a outra perna ainda existe" perigo onFechar={() => setModal(null)} ocupado={ocupado === 'acao'}
          rodape={<>
            <button className="btn btn-secondary me-auto" onClick={() => setModal(null)} disabled={ocupado === 'acao'}>Deixar como está</button>
            <button className="btn btn-danger" disabled={ocupado === 'acao'}
              onClick={() => executar({ acao: 'excluir', nCodLanc: modal.perna.nCodLanc, conta: modal.perna.conta, valor: modal.perna.valor, data: modal.perna.data })}>
              <i className="fas fa-trash me-2"></i>Excluir a outra perna ({modal.perna.nCodLanc})
            </button>
          </>}>
          <div className="small">
            A perna na conta <b>{modal.perna.conta}</b> (código {modal.perna.nCodLanc}, {dataBr(modal.perna.data)}, {brl(modal.perna.valor)}) continua no Omie. Excluir também? É um clique por lançamento.
          </div>
        </Modal>
      )}
    </div></div>
  );
}
